import { HStack, Stack, Text } from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import * as React from "react";
import {
  LuALargeSmall,
  LuCalendar,
  LuClock4,
  LuPlus,
  LuText,
  LuUsers,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import {
  CreateAudienceData,
  createAudience,
  deleteAudience,
  getAudiences,
} from "../api/audience";
import AudienceForm from "../components/audience-form";
import { Button } from "../components/button";
import DataTable from "../components/data-table";
import { DeleteButton } from "../components/delete-button";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../components/dialog";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import Link from "../components/link";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import {
  useAppDispatch,
  useFacebookAdAccounts,
  useLoading,
  useToaster,
  useWorkspaceURL,
} from "../hooks";
import { loadIntegrations } from "../store/workspaceSlice";
import { Audience } from "../types/audience";
import { FacebookAdAccount } from "../types/facebook";

export default function AudiencesPage() {
  const dispatch = useAppDispatch();
  const { workspace } = useParams();
  const workspaceURL = useWorkspaceURL();
  const navigate = useNavigate();
  const [total, setTotal] = React.useState(0);
  const [audiences, setAudiences] = React.useState<Audience[]>([]);
  const [isLoadingAudiences, wrapIsLoadingAudiences] = useLoading();
  const loadAudiences = React.useCallback(async () => {
    await wrapIsLoadingAudiences(async () => {
      const { total, audiences } = await getAudiences(workspace);
      setTotal(total);
      setAudiences(audiences);
    });
  }, [wrapIsLoadingAudiences, workspace]);
  React.useEffect(() => {
    loadAudiences();
    dispatch(loadIntegrations());
  }, [dispatch, loadAudiences]);

  const {
    facebookAdAccounts,
    isLoadingFacebookAdAccounts,
    loadFacebookAdAccounts,
  } = useFacebookAdAccounts(workspace);
  React.useEffect(() => {
    loadFacebookAdAccounts();
  }, [loadFacebookAdAccounts]);

  const wrapToaster = useToaster();
  const [isCreatingAudience, wrapIsCreatingAudience] = useLoading();
  const onCreateAudience = React.useCallback(
    async (data: CreateAudienceData) => {
      await wrapToaster(async () => {
        await wrapIsCreatingAudience(async () => {
          const { id } = await createAudience(workspace, data);
          navigate(workspaceURL(`audiences/${id}`));
        });
      });
    },
    [navigate, wrapToaster, wrapIsCreatingAudience, workspaceURL, workspace],
  );
  const onDeleteAudience = React.useCallback(
    async (audience: Audience) => {
      await wrapToaster(async () => {
        await wrapIsLoadingAudiences(async () => {
          await deleteAudience(workspace, audience.id);
          await loadAudiences();
        });
      });
    },
    [loadAudiences, wrapToaster, wrapIsLoadingAudiences, workspace],
  );

  return (
    <Page loading={isLoadingAudiences}>
      <Stack flex="1">
        <PageHeader
          title="Audiences"
          subtitle="Create and manage custom intent based Audiences"
        >
          {total > 0 && (
            <CreateAudienceDialog
              adAccounts={facebookAdAccounts}
              loading={isCreatingAudience}
              loadingAdAccounts={isLoadingFacebookAdAccounts}
              onSubmit={onCreateAudience}
              triggerSize="xs"
            />
          )}
        </PageHeader>
        {total === 0 ? (
          <PageBody>
            <HStack flex="1" alignItems="center">
              <EmptyState
                icon={<IconCircle Icon={LuUsers} size="md" variant="flat" />}
                title="No Audiences"
                description="Add your first audience to get started."
              >
                <CreateAudienceDialog
                  adAccounts={facebookAdAccounts}
                  loading={isCreatingAudience}
                  loadingAdAccounts={isLoadingFacebookAdAccounts}
                  onSubmit={onCreateAudience}
                />
              </EmptyState>
            </HStack>
          </PageBody>
        ) : (
          <Stack align="top" flex="1" gap="1">
            <DataTable
              data={audiences}
              columns={[
                {
                  id: "name",
                  name: "Name",
                  icon: <LuALargeSmall />,
                  width: 180,
                  cell: ({ value: audience }) => {
                    return (
                      <Link to={workspaceURL(`audiences/${audience.id}`)}>
                        {audience.name}
                      </Link>
                    );
                  },
                },
                {
                  id: "description",
                  name: "Description",
                  icon: <LuText />,
                  width: 180,
                },
                {
                  id: "retention",
                  name: "Retention",
                  icon: <LuClock4 />,
                  width: 160,
                },
                {
                  id: "created-at",
                  name: "Created",
                  icon: <LuCalendar />,
                  width: 210,
                  cell: ({ value: audience }) => {
                    return (
                      <Text>
                        {formatDistanceToNow(audience["created-at"], {
                          addSuffix: true,
                        })}
                      </Text>
                    );
                  },
                },
                {
                  width: 120,
                  cell: ({ value: audience }) => {
                    return (
                      <DeleteButton
                        size="2xs"
                        colorPalette="gray"
                        label="Delete Audience"
                        message="Delete this Audience?"
                        onDelete={() => onDeleteAudience(audience)}
                      />
                    );
                  },
                },
              ]}
            />
          </Stack>
        )}
      </Stack>
    </Page>
  );
}

interface CreateAudienceDialogProps {
  adAccounts: FacebookAdAccount[];
  loading?: boolean;
  loadingAdAccounts?: boolean;
  onSubmit: (data: CreateAudienceData) => void;
  triggerSize?: "xs" | "sm" | "md";
}

function CreateAudienceDialog({
  adAccounts,
  loading,
  loadingAdAccounts,
  onSubmit,
  triggerSize,
}: CreateAudienceDialogProps) {
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  return (
    <DialogRoot size="sm">
      <DialogTrigger asChild>
        <Button size={triggerSize}>
          <LuPlus /> Create Audience
        </Button>
      </DialogTrigger>
      <DialogContent bg="bg.muted" ref={dialogContentRef}>
        <AudienceForm
          adAccounts={adAccounts}
          loadingAdAccounts={loadingAdAccounts}
          onSubmit={(data) =>
            onSubmit({
              ...data,
              retention: data.retention[0],
              "fb-ad-account-id": data.fbAdAccountId[0],
            })
          }
          portalRef={dialogContentRef}
          wrap={(content) => (
            <>
              <DialogHeader bg="bg" borderTopRadius="md">
                <HStack>
                  <IconCircle Icon={LuUsers} size="md" />
                  <DialogTitle>Create Audience</DialogTitle>
                </HStack>
              </DialogHeader>
              <DialogBody bg="bg" borderBottomRadius="md" borderBottomWidth="1">
                {content}
              </DialogBody>
              <DialogFooter pt="4">
                <DialogActionTrigger asChild>
                  <Button colorPalette="gray" variant="surface">
                    Cancel
                  </Button>
                </DialogActionTrigger>
                <Button loading={loading} type="submit">
                  Create
                </Button>
              </DialogFooter>
              <DialogCloseTrigger colorPalette="gray" />
            </>
          )}
        />
      </DialogContent>
    </DialogRoot>
  );
}
